import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout } from '@/components/layout';
import { PermissionGuard } from '@/components/layout/permission/PermissionGuard';
import { LabelInput, TextArea } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { SHIFT_REQUEST_STATUS, SHIFT_COMMENT_MAX_LENGTH } from '@/api/domains/shift/shift.types';
import type { CompanyShiftRequest } from '@/api/domains/shift/shift.types';
import { useShiftChangeRequestResolution } from '@/hooks/attendance/useShiftChangeRequestResolution';


interface ShiftChangeRequestDetailPanelProps {
  request: CompanyShiftRequest;
  // Aprobación exitosa: cierra el panel y refresca listado + KPIs.
  onApproved?: () => void;
  // Rechazo exitoso: cierra el panel y refresca listado + KPIs.
  onRejected?: () => void;
  // Conflicto de concurrencia: refresca listado + KPIs (el panel queda en solo lectura).
  onConflict?: () => void;
}

/**
 * Panel lateral con el detalle de una solicitud de cambio de turno.
 *
 * - Estado Pendiente: muestra el campo obligatorio "Comentario aprobador"
 *   y las acciones Aprobar/Rechazar. Ambos botones son funcionales y comparten
 *   el mismo campo de comentario. Se habilitan solo cuando hay comentario.
 * - Estados Aprobada/Rechazada: solo lectura, con el bloque de resolución (fecha, aprobador
 *   y comentario del aprobador). Tras un conflicto de concurrencia el panel pasa a este modo.
 * - Estado Cancelada: solo lectura, sin acciones ni bloque de resolución.
 */
export const ShiftChangeRequestDetailPanel: React.FC<ShiftChangeRequestDetailPanelProps> = ({
  request,
  onApproved,
  onRejected,
  onConflict,
}) => {
  const { t: translate } = useTranslation();

  const {
    displayRequest,
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleApprove,
    handleReject,
  } = useShiftChangeRequestResolution({ request, onApproved, onRejected, onConflict });

  const isResolved =
    displayRequest.statusCode === SHIFT_REQUEST_STATUS.APPROVED ||
    displayRequest.statusCode === SHIFT_REQUEST_STATUS.REJECTED;
  const isPending = displayRequest.statusCode === SHIFT_REQUEST_STATUS.PENDING;

  return (
    <SidePanelFormLayout data-testid="shift-change-request-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.id')}
          value={String(displayRequest.id)}
          disabled
          readOnly
          data-testid="shift-detail-id"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.status')}
          value={displayRequest.status}
          disabled
          readOnly
          data-testid="shift-detail-status"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.rut')}
          value={displayRequest.employeeRut}
          disabled
          readOnly
          data-testid="shift-detail-rut"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.employee')}
          value={displayRequest.employeeName}
          disabled
          readOnly
          data-testid="shift-detail-employee"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.requestDate')}
          value={displayRequest.requestDate}
          disabled
          readOnly
          data-testid="shift-detail-request-date"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.dateFrom')}
          value={displayRequest.dateFrom}
          disabled
          readOnly
          data-testid="shift-detail-date-from"
        />
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.table.dateTo')}
          value={displayRequest.dateTo}
          disabled
          readOnly
          data-testid="shift-detail-date-to"
        />
        {/*
          Bloque de resolución (solo Aprobada/Rechazada), justo debajo de "Fecha hasta":
          Fecha de revisión → Aprobador → Comentario aprobador.
        */}
        {isResolved && (
          <>
            <LabelInput
              label={translate('attendance:shiftChangeAdmin.detail.reviewDate')}
              value={displayRequest.reviewDate}
              disabled
              readOnly
              data-testid="shift-detail-review-date"
            />
            <LabelInput
              label={translate('attendance:shiftChangeAdmin.detail.reviewerName')}
              value={displayRequest.reviewerName}
              disabled
              readOnly
              data-testid="shift-detail-reviewer-name"
            />
            <LabelInput
              label={translate('attendance:shiftChangeAdmin.detail.approvalComment')}
              value={displayRequest.approvalComment}
              disabled
              readOnly
              data-testid="shift-detail-approval-comment"
            />
          </>
        )}
        {/* Comentario solicitante: siempre visible, justo debajo del Aprobador. */}
        <LabelInput
          label={translate('attendance:shiftChangeAdmin.detail.requesterComment')}
          value={displayRequest.requesterComment}
          disabled
          readOnly
          data-testid="shift-detail-requester-comment"
        />
        {/*
          Estado Pendiente: comentario del aprobador OBLIGATORIO.
          Campo compartido por los botones Aprobar y Rechazar.
        */}
        {isPending && (
          <TextArea
            label={translate('attendance:shiftChangeAdmin.detail.approvalComment')}
            placeholder={translate('attendance:shiftChangeAdmin.detail.approverCommentPlaceholder')}
            value={comment}
            onChange={setComment}
            required
            maxLength={SHIFT_COMMENT_MAX_LENGTH}
            showCharacterCount
            data-testid="shift-detail-approver-comment-input"
          />
        )}
      </SidePanelFormLayout.Fields>

      {/*
        Solo estado Pendiente: acciones Aprobar/Rechazar.
        Ambos se habilitan únicamente cuando hay comentario del aprobador.
        El estado de carga deshabilita ambos botones mientras la operación está en curso.
      */}
      {isPending && (
        <SidePanelFormLayout.Footer>
          <div className="flex w-full gap-3">
            <PermissionGuard action="edit">
              <PrimaryButton
                type="button"
                colorVariant="success"
                fullWidth
                disabled={!canSubmit}
                isLoading={isSubmitting}
                onClick={handleApprove}
                data-testid="shift-detail-approve"
              >
                {translate('attendance:shiftChangeAdmin.detail.approve')}
              </PrimaryButton>
            </PermissionGuard>
            <PermissionGuard action="edit">
              <PrimaryButton
                type="button"
                variant="outline"
                colorVariant="error"
                fullWidth
                disabled={!canSubmit}
                isLoading={isSubmitting}
                onClick={handleReject}
                data-testid="shift-detail-reject"
              >
                {translate('attendance:shiftChangeAdmin.detail.reject')}
              </PrimaryButton>
            </PermissionGuard>
          </div>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default ShiftChangeRequestDetailPanel;
